/// <reference path="registerPaymentSectionBase.ts" />
module Meevo.Plugins.Register {
    "use strict";

    export class RegisterCreditPaymentSectionBase extends Meevo.Plugins.Register.RegisterPaymentSectionBase {
        payment: Model.SaleCreditCardPayment;
        showAddPaymentButton: boolean = false;

        get $translate() { return this.registerContext.$translate; }
        get $filter() { return this.registerContext.$filter; }
        get $q() { return this.registerContext.$q; }
        get $timeout() { return this.registerContext.$timeout; }
        get $rootScope() { return this.registerContext.$rootScope; }

        get dataService() { return this.registerContext.dataService; }
        get modalService() { return this.registerContext.modalService; }
        get busyService() { return this.registerContext.busyService; }
        get hostedPageService() { return this.registerContext.hostedPageService; }

        get creditCardProcessingEnabled() {
            return this.registerContext.creditCardProcessingEnabled;
        }

        isHostedPageActive(hostedPageId: Meevo.Model.HostedPageIdType) {
            return this.hostedPageService.getHostedPageActive(hostedPageId);
        }

        protected returnToOptions() {
            const returnSection = this.registerContext.isCreditMode
                ? RegisterSectionTypeEnum.ChangeOptions
                : RegisterSectionTypeEnum.PaymentOptions;

            return this.registerContext.selectSection(returnSection);
        }

        protected getNewPayment(paymentCategory: Model.PaymentCategoryEnum = Model.PaymentCategoryEnum.CreditDebit): any {
            return {
                __type: "Millennium.Meevo.Register.Contracts.Model.SalePaymentCreditCard, Millennium.Meevo.Register.Contracts.Model",
                amount: 0,
                taxAmount: 0,
                isPending: this.creditCardProcessingEnabled || paymentCategory === Model.PaymentCategoryEnum.CareCredit,
                paymentCategory: paymentCategory,
                paymentRequestId: null,
                transactionSetupId: null,
                tempId: Guid.newGuid().toString(),
            };
        }

        protected getProcessedPaymentMessage(processedPayment): string {
            let message: string;
            if (processedPayment.creditCardProcessorStatus.message) {
                message = this.$translate.instant(processedPayment.creditCardProcessorStatus.message);
                if (message === processedPayment.creditCardProcessorStatus.message) {
                    message = this.$translate.instant("MESSAGE_CC_STATUS_UNKNOWN") + ` (${processedPayment.creditCardProcessorStatus.message})`;
                }
            } else {
                message = this.$translate.instant("MESSAGE_CC_STATUS_UNKNOWN");
            }
            message += this.registerContext.appendPaymentAlertMessage(processedPayment.creditCardProcessorStatus);
            if (processedPayment.creditCardProcessorStatus.processorMessage) {
                let processorDetailsHeading = this.$translate.instant('LABEL_PROCESSOR_DETAILS');
                // add translated heading and detail
                message = message + '<br/><b>' + processorDetailsHeading + ':</b> ' + processedPayment.creditCardProcessorStatus.processorMessage.replace("\n", "<br/>");
            }
            return message;
        }

        // overridden by each concrete payment section (CreditPaymentsSection, CareCreditPaymentsSection)
        // with its own real handling; declared here only so reconcilePendingPayment() below can
        // invoke it polymorphically.
        protected processPaymentSuccessCallback = (response) => {
        };

        protected processPaymentErrorCallback = (response, allowReconcile: boolean = false) => {
            switch (response.status) {
                case 408: //timeout
                    this.registerContext.removePendingTicketPaymentLine();
                    this.busyService.stop();
                    return this.modalService.alert('MESSAGE_CC_STATUS_REQUESTTIMEOUT').finally(() => {
                        return this.returnToOptions();
                    });

                case 409: //conflict
                    this.registerContext.removePendingTicketPaymentLine();
                    this.busyService.stop();
                    return this.modalService.alert(response.data.responseStatus.message);
            }

            if (allowReconcile && response.isUnknownError) {
                // response gave us no usable outcome (e.g. the request never reached the server,
                // or its response never made it back) -- the underlying charge may have actually
                // succeeded, so try to reconcile against any recoverable hold before giving up on it.
                return this.reconcilePendingPayment();
            }

            this.registerContext.removePendingTicketPaymentLine();
            this.busyService.stop();
            this.reset();
        };

        private nonAnonymousClientTypes = [
            Model.ClientTypeEnum.AnonymousOther,
            Model.ClientTypeEnum.AnonymousFemale,
            Model.ClientTypeEnum.AnonymousMale
        ];

        private getFirstNamedClient() {
            return _.find(this.registerContext.allUniqueClients, (registerClient: RegisterClient) =>
                registerClient.client && this.nonAnonymousClientTypes.indexOf(registerClient.clientType) === -1);
        }

        private concludePaymentFailed() {
            this.registerContext.removePendingTicketPaymentLine();
            this.busyService.stop();
            this.reset();
        }

        private promptRetryOrRemove(): ng.IPromise<any> {
            this.busyService.stop();
            return this.modalService.confirmHandler("MESSAGE_PAYMENT_STATUS_UNKNOWN_RETRY").then((retry) => {
                if (retry) {
                    this.busyService.start();
                    return this.reconcilePendingPayment();
                }

                return this.concludePaymentFailed();
            });
        }

        protected reconcilePendingPayment(): ng.IPromise<any> {
            const paymentLine: Model.SaleCreditCardPayment = _.findWhere(this.registerContext.sale.paymentLines, { isPending: true });

            if (!paymentLine) {
                this.busyService.stop();
                this.reset();
                return this.$q.resolve();
            }

            const namedClient = this.getFirstNamedClient();

            const request = {
                pageNumber: 0,
                itemsPerPage: namedClient ? 20 : 200,
                sortBy: '',
                sortDirection: Model.SortDirection.Ascending,
                includeActive: true,
                includeDeleted: false,
                includeInactive: false,
                criteria: {
                    holdTypes: [Model.HoldTypeEnum.Register, Model.HoldTypeEnum.Recovery],
                    clientIds: namedClient ? [namedClient.client.id] : []
                }
            };

            return this.dataService.getListPaged(this.registerContext.appSettings.API_ENDPOINTS.saleHold, request)
                .then((holds: any[]) => {
                    let matchingHold: any = null;
                    let matchingPaymentLine: any = null;

                    for (const hold of holds) {
                        const found = hold.sale && _.find(hold.sale.paymentLines, (line: any) =>
                            line.tempId && paymentLine.tempId && String(line.tempId).toLowerCase() === String(paymentLine.tempId).toLowerCase());
                        if (found) {
                            matchingHold = hold;
                            matchingPaymentLine = found;
                            break;
                        }
                    }

                    if (matchingPaymentLine) {
                        return this.dataService.customMethodData(`api/${this.registerContext.appSettings.API_ENDPOINTS.saleHold}/${matchingHold.id}/Lock`, "GET", null)
                            .then((lockResponse: any) => {
                                // terminal is still in use
                                if (lockResponse.data) {
                                    return this.promptRetryOrRemove();
                                }

                                // the hold itself is only a snapshot from when it was created, so its
                                // own copy of the payment request won't reflect a status (e.g. Approved)
                                // that only became final afterward -- fetch the current one before
                                // running it through the same workflow the happy path uses.
                                return this.dataService.customMethodData(`api/paymentRequest/${matchingPaymentLine.paymentRequestId}`, "GET", null)
                                    .then((paymentRequestResponse: any) => {
                                        const currentPaymentRequest = paymentRequestResponse.data;
                                        // the fresh lookup is just the PaymentRequest row -- it doesn't know
                                        // which hold it came from, but processPaymentSuccessCallback needs
                                        // onHoldId/holdType to sync the sale's hold tracking, so carry those
                                        // over from the hold we actually found it on.
                                        currentPaymentRequest.onHoldId = matchingHold.id;
                                        currentPaymentRequest.holdType = matchingHold.holdType;
                                        return this.$q.when(this.processPaymentSuccessCallback({ status: 200, data: currentPaymentRequest }));
                                    });
                            })
                            .catch(() => this.promptRetryOrRemove())
                            .finally(() => this.busyService.stop());
                    }

                    // no hold matched this payment's tempId at all, so there's nothing specific
                    // to check the lock state of -- the payment genuinely never went through.
                    return this.concludePaymentFailed();
                })
                .catch(() => this.promptRetryOrRemove());
        }
    }
}
